// @ts-check
'use_strict';
import { load_js_async } from '../tv_utils/utils.js';

/** Gloabal variable from Google Recaptcha library. */
declare var grecaptcha;

const recaptcha_url = 'https://www.google.com/recaptcha/api.js?render=explicit';

/**
 * Function to render or reset the given Google Recaptcha.
 */
function render_grecaptcha(recaptcha_element: HTMLElement) {
    // Google Recaptcha has already been rendered.
    if (recaptcha_element.childElementCount > 0) {
        // Find the Recaptcha response to add the necessary CSS class for validation.
        const recaptcha_response = recaptcha_element.querySelector('.g-recaptcha-response');
        recaptcha_response.classList.add('tv-recaptcha__response-required');
    } else {
        // Have to wait for the Recaptcha to render.
        const google_observer = new MutationObserver(() => {
            // Find the Recaptcha response to add the necessary CSS class for validation.
            const recaptcha_response = recaptcha_element.querySelector('.g-recaptcha-response');
            recaptcha_response.classList.add('tv-recaptcha__response-required');
            google_observer.disconnect();
        });
        google_observer.observe(recaptcha_element, { childList: true });

        // Render Google Recaptcha.
        const widget_id = grecaptcha.render(recaptcha_element, { 'sitekey': recaptcha_element.dataset.sitekey });
        // Set widget ID.
        recaptcha_element.setAttribute('id', widget_id);
    }
}

/**
 * Loads the reCAPTCHA script and renders the given element.
 */
async function load_and_render(recaptcha_element: HTMLElement): Promise<void> {
    if (typeof grecaptcha === 'undefined') {
        if (!await load_js_async(recaptcha_url)) {
            return;
        }
    }
    grecaptcha.ready(() => {
        render_grecaptcha(recaptcha_element);
    });
}

/**
 * Observes a reCAPTCHA element and loads/renders it when it becomes visible.
 * Uses a rootMargin to begin loading before the element fully enters the viewport.
 */
function observe_recaptcha(recaptcha_element: HTMLElement): void {
    if (recaptcha_element.dataset.recaptchaObserved) {
        return;
    }
    recaptcha_element.dataset.recaptchaObserved = 'true';

    const observer = new IntersectionObserver((entries, obs) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                obs.unobserve(recaptcha_element);
                load_and_render(recaptcha_element);
            }
        });
    }, { threshold: 0, rootMargin: '200px' });
    observer.observe(recaptcha_element);
}

/**
 * This function is to initialize our Google Recaptcha styling changes to allow Recaptcha validation.
 * If no element is given, then all available Recaptcha elements are found and rendered.
 * Uses IntersectionObserver to defer loading until the element is visible.
 *
 * @param {HTMLElement|null} recaptcha_element is the element provided to get rendered or reset.
 * @return {void}
 */
export function init_g_recaptcha(recaptcha_element: HTMLElement = null): void {
    if (recaptcha_element) {
        observe_recaptcha(recaptcha_element);
    } else {
        const recaptcha_elements = <HTMLCollectionOf<HTMLElement>>document.getElementsByClassName('g-recaptcha');
        if (!recaptcha_elements.length) {
            return;
        }
        for (const el of Array.from(recaptcha_elements)) {
            observe_recaptcha(el);
        }
    }
}